import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Crown, Lock, Sparkles, ArrowRight, Camera } from 'lucide-react';

export function UpgradeModal({ isOpen, onClose, feature = 'premium features' }) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const featureDescriptions = {
    'bank_sync': {
      title: 'Bank Sync',
      description: 'Automatically import your transactions and bills with secure Plaid integration.',
      icon: '🏦'
    },
    'full_history': {
      title: 'Full History Access',
      description: 'View your complete transaction and envelope history beyond the current month.',
      icon: '📊'
    },
    'advanced_ai': {
      title: 'Advanced AI Insights',
      description: 'Get personalized financial advice and deep insights from Guapo AI.',
      icon: '🤖'
    },
    '1099_tax_tracking': {
      title: '1099 Tax Tracker',
      description: 'Track your self-employment income month-by-month and get automatic YTD tax estimates when you wrap up each month.',
      icon: '📋'
    },
    'guapo_vision': {
      title: 'Guapo Vision',
      description: 'Stop typing out your balances manually. Snap a pic or upload a PDF of your credit card statement. Guapo will read it and update your balances, APRs, and due dates instantly.',
      icon: '📸'
    },
    'default': {
      title: 'Premium Feature',
      description: 'Unlock this feature and many more with a premium subscription.',
      icon: '⭐'
    }
  };

  const featureInfo = featureDescriptions[feature] || featureDescriptions.default;

  // Special styling for Guapo Vision
  const isGuapoVision = feature === 'guapo_vision';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className={`p-6 text-white relative ${
          isGuapoVision 
            ? 'bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500'
            : 'bg-gradient-to-r from-purple-600 to-indigo-600'
        }`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
          
          {isGuapoVision ? (
            /* Guapo Vision Special Header */
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-6 h-6" />
                <Sparkles className="w-5 h-5 text-yellow-300" />
              </div>
              <h2 className="text-2xl font-bold">Unlock Guapo Vision! 📸✨</h2>
            </div>
          ) : (
            /* Default Header */
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Lock className="w-6 h-6" />
                <span className="font-bold text-lg">Premium Feature</span>
              </div>
              <h2 className="text-2xl font-bold">{featureInfo.title}</h2>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl ${
              isGuapoVision ? 'bg-emerald-100' : 'bg-purple-100'
            }`}>
              {featureInfo.icon}
            </div>
            <p className="text-gray-600 flex-1">{featureInfo.description}</p>
          </div>

          {/* Benefits */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Premium Includes:
            </h3>
            <ul className="space-y-2">
              {/* Guapo Vision - TOP HIGHLIGHTED FEATURE */}
              <li className={`flex items-center gap-2 text-sm ${
                isGuapoVision ? 'text-emerald-700 font-semibold bg-emerald-50 -mx-2 px-2 py-1 rounded-lg' : 'text-gray-700'
              }`}>
                <Sparkles className={`w-4 h-4 ${isGuapoVision ? 'text-emerald-500' : 'text-purple-500'}`} />
                Guapo Vision - AI Statement Scanner 📸
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Plaid Bank Sync - Auto-import transactions
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Full History Access - All your data, anytime
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Advanced AI Insights - Personalized advice
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Sparkles className="w-4 h-4 text-purple-500" />
                1099 Tax Calculator - Self-employment tools
              </li>
            </ul>
          </div>

          {/* Pricing hint */}
          <p className="text-center text-gray-500 text-sm mb-4">
            Starting at just <span className="font-bold text-gray-900">$6.99/month</span>
          </p>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => {
                onClose();
                navigate('/app/pricing');
              }}
              className={`w-full py-3 font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${
                isGuapoVision
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white'
                  : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white'
              }`}
              data-testid="upgrade-now-btn"
            >
              Upgrade to Premium
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors"
              data-testid="keep-manual-btn"
            >
              {isGuapoVision ? 'Keep Entering Manually' : 'Maybe Later'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpgradeModal;
